import { useEffect, useRef, useState } from 'react';

interface UseViewportAutoplayOptions {
  imageCount: number;
  intervalMs?: number;
  threshold?: number; // Percentage of element that needs to be visible (0-1)
}

export const useViewportAutoplay = ({
  imageCount,
  intervalMs = 4000,
  threshold = 0.7
}: UseViewportAutoplayOptions) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [isInViewport, setIsInViewport] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const elementRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for viewport detection
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting && entry.intersectionRatio >= threshold;
        setIsInViewport(isVisible);
      },
      {
        threshold: threshold,
        rootMargin: '0px'
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold]);

  // Auto-play functionality based on viewport visibility
  useEffect(() => {
    if (isInViewport && imageCount > 1) {
      setIsAutoPlaying(true);
      intervalRef.current = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % imageCount);
      }, intervalMs);
    } else {
      setIsAutoPlaying(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isInViewport, imageCount, intervalMs]);

  const handlePreviousImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? imageCount - 1 : prev - 1
    );
    setIsAutoPlaying(false);
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % imageCount);
    setIsAutoPlaying(false);
  };

  const handleImageClick = (index: number) => {
    setCurrentImageIndex(index);
    setIsAutoPlaying(false);
  };

  return {
    currentImageIndex,
    isAutoPlaying,
    isInViewport,
    elementRef,
    handlePreviousImage,
    handleNextImage,
    handleImageClick
  };
};
